import { useState, useEffect } from 'react';
import { Info, Lock } from 'lucide-react';
import { useConfigTab, FORM_STYLES, Toggle, ConfigTimestamps, LoadingState } from './shared';

export function NdrInteractionTab({ merchantId, reloadKey }) {
  const { loading, saving, isNew, configValue, metadata, saveConfig } = useConfigTab(
    merchantId, 'MERCHANT_NDR_CUSTOMER_INTERACTION_CONFIGURATIONS', reloadKey,
  );

  const [ndrEnabled, setNdrEnabled] = useState(false);
  const [channelPriority, setChannelPriority] = useState([]);
  const [manualCallEnabled, setManualCallEnabled] = useState(false);
  const [manualCallChargeAmount, setManualCallChargeAmount] = useState('');
  const [manualCallChargeCurrency, setManualCallChargeCurrency] = useState('');

  const wasEnabled = configValue?.ndr_customer_interaction_enabled === true;

  useEffect(() => {
    if (!configValue) {
      setNdrEnabled(false);
      setChannelPriority([]);
      setManualCallEnabled(false);
      setManualCallChargeAmount('');
      setManualCallChargeCurrency('');
      return;
    }
    setNdrEnabled(configValue.ndr_customer_interaction_enabled === true);
    setChannelPriority(Array.isArray(configValue.interaction_method_priority) ? configValue.interaction_method_priority : []);

    const mc = configValue.manual_call_config || {};
    setManualCallEnabled(mc.enabled === true);
    const charge = mc.charge;
    setManualCallChargeAmount(charge && charge.amount != null ? String(charge.amount) : '');
    setManualCallChargeCurrency(charge && charge.currency ? charge.currency : '');
  }, [configValue]);

  const handleNdrToggle = (val) => {
    setNdrEnabled(val);
    if (val && !wasEnabled && manualCallChargeAmount === '') {
      setManualCallChargeAmount('5');
      setManualCallChargeCurrency('INR');
    }
  };

  const handleSave = () => {
    const payload = {
      ndr_customer_interaction_enabled: ndrEnabled,
      interaction_method_priority: channelPriority.length > 0 ? channelPriority : undefined,
      manual_call_config: {
        enabled: manualCallEnabled,
        charge: manualCallChargeAmount !== '' ? {
          amount: parseFloat(manualCallChargeAmount),
          currency: manualCallChargeCurrency || 'INR',
        } : undefined,
      },
    };

    if (configValue) {
      if (configValue.whatsapp_config || configValue.whats_app_ndr_configuration) {
        payload.whatsapp_config = configValue.whatsapp_config || configValue.whats_app_ndr_configuration;
      }
      if (configValue.ivr_config || configValue.ivr_ndr_configuration) {
        payload.ivr_config = configValue.ivr_config || configValue.ivr_ndr_configuration;
      }
      if (configValue.delivery_attempt_configs || configValue.per_attempt_configuration) {
        payload.delivery_attempt_configs = configValue.delivery_attempt_configs || configValue.per_attempt_configuration;
      }
    }

    saveConfig({ merchant_ndr_customer_interaction_configurations_value: payload });
  };

  if (loading) return <LoadingState />;

  return (
    <div className="space-y-4">
      {isNew && (
        <div className="flex items-center gap-2 px-3 py-2.5 bg-blue-50 border border-blue-100 rounded-lg text-xs sm:text-sm text-blue-700">
          <Info className="w-4 h-4 shrink-0" />
          No existing configuration - configure manual call settings below and save.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className={FORM_STYLES.sectionCard}>
          <h3 className={FORM_STYLES.sectionTitle}>NDR Customer Interaction</h3>
          <Toggle
            id="ndr-enabled"
            checked={ndrEnabled}
            onChange={handleNdrToggle}
            label="NDR Interaction Enabled"
            description="Enable automated customer interaction for Non-Delivery Reports"
          />
        </div>

        <div className={FORM_STYLES.sectionCard}>
          <h3 className={FORM_STYLES.sectionTitle}>Manual Call Configuration</h3>
          <div className="space-y-3">
            <Toggle
              id="manual-call-enabled"
              checked={manualCallEnabled}
              onChange={setManualCallEnabled}
              label="Manual Call Enabled"
              description="Enable manual call attempts for NDR resolution"
            />
            <div>
              <label className={FORM_STYLES.label}>Charge</label>
              <input type="text" value={manualCallChargeAmount ? `₹${manualCallChargeAmount} INR` : ''} readOnly disabled className={FORM_STYLES.inputDisabled} />
              <p className="text-[11px] text-slate-400 mt-1">*Inclusive of GST. Contact support to help.</p>
            </div>
          </div>
        </div>
      </div>

      <div className={FORM_STYLES.sectionCard + ' opacity-60'}>
        <h3 className={FORM_STYLES.sectionTitle}>
          <Lock className="w-3.5 h-3.5 text-slate-400" />
          Global Channel Priority
          <span className="ml-auto text-[10px] font-medium text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">Locked</span>
        </h3>
        <p className="text-[11px] text-slate-400 mb-2">Channel priority is managed by admin. Contact support to change.</p>
        {channelPriority.length === 0 ? (
          <p className="text-xs text-slate-400 italic">No channels configured.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {channelPriority.map((channel, idx) => (
              <div key={idx} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 rounded-lg border border-slate-100">
                <span className="text-[11px] font-bold text-slate-400">{idx + 1}.</span>
                <span className="text-xs text-slate-500 font-medium">{channel}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-3 border-t border-slate-100">
        <button type="button" onClick={handleSave} disabled={saving} className={FORM_STYLES.btnPrimary}>
          {saving
            ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            : <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
          }
          {saving ? 'Saving…' : 'Save Configuration'}
        </button>
        {!isNew && <ConfigTimestamps metadata={metadata} />}
      </div>
    </div>
  );
}
